import { Location, NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbCollapse, NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { DateRangePickerComponent } from 'components/date-range-picker/date-range-picker.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { Observable, forkJoin } from 'rxjs';
import { ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { ClaimDirective } from 'src/app/directive/claim.directive';
import { Breadcrumb } from 'types/breadcrumb.interface';
import { RoleListItem, StructuralUnitRoleListItem, StructuralUnitRoleListItemPagedResponse } from 'types/role.interface';
import { StructuralUnitMinimalListItem } from 'types/structural-unit.interface';
import {
  AbsenceFormItem,
  CreateUserAbsenceRequest,
  UpdateUserAbsenceRequest,
  UpdateUserMembershipItem,
  UpdateUserRequest,
  UserAbsenceListItem,
  UserCard,
  UserListItem,
  CLAIM_TYPES,
  CLAIM_SCOPES,
  UserMinimalListItem
} from 'types/user.interface';
import { formatDatetime, isDateAfterToday, isDateBeforeToday, isDateTodayOrAfter } from 'util/date.utility';
import { getAllFormErrors, roleStructuralUnitsValidator } from 'util/form.utility';
import { checkClaims } from 'util/user.utility';
import { TooltipComponent } from 'components/tooltip/tooltip.component';

export interface SelectOption {
  value: string;
  label: string;
}

export const SYSTEM_USER_ROLE_NAME = 'Sistēmas lietotājs';

@Component({
  selector: 'app-edit-backoffice-user-page',
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    TranslateModule,
    NgClass,
    SpinnerComponent,
    DateRangePickerComponent,
    FormsModule,
    ReactiveFormsModule,
    NgbCollapse,
    ConfirmModalComponent,
    ClaimDirective,
    TooltipComponent
  ],
  templateUrl: './edit-backoffice-user-page.component.html',
  styleUrl: './edit-backoffice-user-page.component.scss'
})
export class EditBackofficeUserPageComponent implements OnInit {
  public readonly isDateBeforeToday = isDateBeforeToday;
  public readonly isDateAfterToday = isDateAfterToday;
  public readonly isDateTodayOrAfter = isDateTodayOrAfter;
  public readonly formatDatetime = formatDatetime;
  public readonly CLAIM_TYPES = CLAIM_TYPES;
  public readonly POLICY_TYPE_ROLE = CLAIM_SCOPES['role'];
  public readonly POLICY_TYPE_USER = CLAIM_SCOPES['user'];
  public readonly POLICY_TYPE_ABSENCE = CLAIM_SCOPES['absence'];

  private roleIdSubs: Array<ReturnType<Observable<any>['subscribe']>> = [];

  breadcrumbs: Breadcrumb[] = [
    {
      title: 'components.breadcrumbs.admin',
      url: ''
    },
    {
      title: 'components.breadcrumbs.users',
      url: '/lietotaji'
    }
  ];
  userId: string | null = null;
  user?: UserCard;
  originalAbsences: UserAbsenceListItem[] = [];
  absences: AbsenceFormItem[] = [];
  isUserLoading: boolean = true;
  isAbsencesLoading: boolean = true;
  isAllUsersLoading: boolean = true;
  isSubmitDeletionLoading: boolean = false;
  isSubmitUserLoading: boolean = false;
  isSubmitAbsencesLoading: boolean = false;
  isOpenConfirmBack: boolean = false;
  substituteOptions: SelectOption[] = [];
  form: FormGroup;
  deleteAbsenceIds: string[] = [];
  isStructuralUnitsCollapsed: Record<number, boolean> = {};
  isHistoricalAbsencesCollapsed: boolean = false;
  systemRoles: RoleListItem[] = [];
  structuralUnitRoles: StructuralUnitRoleListItem[] = [];
  allRoles: (RoleListItem | StructuralUnitRoleListItem)[] = [];
  isSystemRolesLoading: boolean = true;
  isStructuralUnitRolesLoading: boolean = true;
  isStructuralUnitsLoading: boolean = true;
  structuralUnitOptions: StructuralUnitMinimalListItem[] = [];
  submitAttempted: boolean = false;
  applicantUserId?: string;
  allUsers?: UserListItem[];

  get absenceEntries(): FormArray {
    return this.form.get('absences') as FormArray;
  }
  get roleEntries(): FormArray {
    return this.form.get('roles') as FormArray;
  }

  get isLoading(): boolean {
    return (
      this.isUserLoading ||
      this.isAbsencesLoading ||
      this.isAllUsersLoading ||
      this.isSystemRolesLoading ||
      this.isStructuralUnitRolesLoading
    );
  }

  get isSubmitLoading(): boolean {
    return this.isSubmitUserLoading || this.isSubmitAbsencesLoading || this.isSubmitDeletionLoading;
  }

  get canEditAbsences(): boolean {
    return this.user?.activationStatus !== 'Blocked';
  }

  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private toastService: ToastService,
    private authService: AuthorizationService
  ) {
    this.form = this.formBuilder.group({});
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.userId = params['id'];

      if (this.userId) {
        if (this.userId === this.authService.getUserId()) {
          this.apiService.getCurrentUser().subscribe(
            (data: UserCard) => {
              this.user = data;
              this.isUserLoading = false;
              this.fillForm();
              this.breadcrumbs.push({
                title: 'components.breadcrumbs.editUser',
                titleArgs: { name: this.user?.fullName },
                url: `/lietotajs/${this.userId}/rediget`
              });
            },
            (error: any) => {
              console.error(error);
            }
          );
        } else {
          this.apiService.getUser(this.userId).subscribe(
            (data: UserCard) => {
              this.user = data;
              this.isUserLoading = false;
              this.fillForm();
              this.breadcrumbs.push({
                title: 'components.breadcrumbs.editUser',
                titleArgs: { name: this.user?.fullName },
                url: `/lietotajs/${this.userId}/rediget`
              });
            },
            (error: any) => {
              console.error(error);
            }
          );
        }

        this.apiService.getAllAbsencesByUserId(this.userId).subscribe(
          (data: UserAbsenceListItem[]) => {
            if (data) {
              this.originalAbsences = data;
              this.absences = this.mapAbsences(data);

              this.sortAbsences();
              this.fillForm();
            }
            this.isAbsencesLoading = false;
          },
          (error: any) => {
            console.error(error);
          }
        );

        this.apiService.getMinimalUsersList().subscribe(
          (data: UserMinimalListItem[]) => {
            this.substituteOptions =
              data
                .sort((a: UserMinimalListItem, b: UserMinimalListItem) => a.fullName!.localeCompare(b.fullName!))
                .filter((user: UserMinimalListItem) => user.id !== this.userId)
                .map(
                  (user: UserMinimalListItem) =>
                    ({
                      label: user.fullName,
                      value: user.id
                    }) as SelectOption
                ) ?? [];
            this.applicantUserId = this.authService.getUserId();
            this.isAllUsersLoading = false;
          },
          (error: any) => {
            console.error(error);
          }
        );

        this.apiService.getSystemRoles().subscribe(
          (data: any) => {
            this.systemRoles = data.list;
            this.systemRoles.sort((a: RoleListItem, b: RoleListItem) => a.name!.localeCompare(b.name!));
            this.isSystemRolesLoading = false;
            this.allRoles = [...this.allRoles, ...this.systemRoles];
          },
          (error: any) => {
            console.error(error);
          }
        );

        this.apiService.getStructuralUnitRoles().subscribe(
          (data: StructuralUnitRoleListItemPagedResponse) => {
            this.structuralUnitRoles = data.list ?? [];
            this.structuralUnitRoles.sort((a: StructuralUnitRoleListItem, b: StructuralUnitRoleListItem) => a.name!.localeCompare(b.name!));
            this.isStructuralUnitRolesLoading = false;
            this.allRoles = [...this.allRoles, ...this.structuralUnitRoles];
          },
          (error: any) => {
            console.error(error);
          }
        );

        this.apiService.getStructuralUnitOptions().subscribe((data: StructuralUnitMinimalListItem[]) => {
          this.structuralUnitOptions = data;
          this.structuralUnitOptions.sort((a: StructuralUnitMinimalListItem, b: StructuralUnitMinimalListItem) =>
            a.name!.localeCompare(b.name!)
          );
          this.isStructuralUnitsLoading = false;
        });
      }
    });
  }

  fillForm(): void {
    const structuralRoles = this.getFormattedStructuralRoles();
    const systemRoles: any[] =
      this.user?.roles?.map((role: any) => ({
        roleId: role.id,
        structuralUnitIds: []
      })) || [];

    const roles = [...structuralRoles, ...systemRoles];

    this.form = this.formBuilder.group({
      activationStatus: [this.user?.activationStatus, Validators.required],
      absences: this.formBuilder.array(
        this.absences.length > 0
          ? this.absences.map((entry: AbsenceFormItem) =>
              this.formBuilder.group({
                id: [entry.id],
                periodStart: [entry.periodStart, Validators.required],
                periodEnd: [entry.periodEnd],
                substituteUserId: [entry.substituteUserId, [Validators.required]],
                applicantUserId: [entry.applicantUserId, Validators.required],
                createdByUserId: [entry.createdByUserId]
              })
            )
          : []
      ),
      roles: this.formBuilder.array(roles.length > 0 ? roles.map((role: any) => this.createRoleFormGroup(role)) : [])
    });

    this.isStructuralUnitsCollapsed = {};
    this.roleEntries.controls.forEach((_, index) => {
      this.isStructuralUnitsCollapsed[index] = true;
    });

    this.roleIdSubs.forEach((s) => s?.unsubscribe());
    this.roleIdSubs = [];

    this.roleEntries.controls.forEach((_, idx) => {
      this.attachRoleChangeHandler(idx);
    });
  }

  createRoleFormGroup(role: any): FormGroup {
    return this.formBuilder.group({
      roleId: [role.roleId, [Validators.required, Validators.min(0)]],
      structuralUnitIds: [role.structuralUnitIds, [roleStructuralUnitsValidator]]
    });
  }

  toggleSectionLoading(isLoading: boolean) {
    this.isSubmitAbsencesLoading = isLoading;
    this.isSubmitUserLoading = isLoading;
    this.isSubmitDeletionLoading = isLoading;
  }

  submit(): void {
    this.toggleSectionLoading(true);
    this.submitAttempted = true;
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.submitAttempted = false;
      this.toggleSectionLoading(false);
      this.toastService.showFormValidationError(getAllFormErrors(this.form), 'pages.user');
    } else {
      this.toggleSectionLoading(true);

      // We delete first to avoid date conflicts with the absences
      if (this.deleteAbsenceIds.length > 0) {
        forkJoin([...this.submitAbsenceDeletion()]).subscribe(
          () => {
            this.handleSubmitUserAndAbsences();
          },
          (error: any) => {
            console.error(error);
            this.toastService.showRequestError();
            this.toggleSectionLoading(false);
          }
        );
      } else {
        this.handleSubmitUserAndAbsences();
      }
    }
  }

  handleSubmitUserAndAbsences(): void {
    const observables = [...this.submitAbsences()];

    if (this.hasEditUserClaim()) {
      observables.push(this.submitUser());
    }

    if (observables.length > 0) {
      forkJoin(observables).subscribe(
        (data: any) => {
          if (data.length > 0) {
            this.deleteAbsenceIds = [];

            data.forEach((element: any) => {
              if (element && element['periodStart']) {
                const absence = this.absences.find((absence: AbsenceFormItem) => absence.periodStart === element.periodStart);

                this.originalAbsences = this.originalAbsences.filter((absence: UserAbsenceListItem) => element.id !== absence.id);
                this.originalAbsences.push(element);

                if (absence) {
                  absence.periodStart = element.periodStart;
                  absence.periodEnd = element.periodEnd;
                  absence.substituteUserId = element.substituteUser.id;
                  absence.id = element.id;
                }
              } else if (element && element.activationStatus) {
                this.user = element;
              } else if (element && element.memberships && this.user) {
                this.user.memberships = element.memberships;
                this.user.roles = element.userRoles;
              }
            });
          }

          this.onSubmitSuccess();
        },
        (error: any) => {
          this.toggleSectionLoading(false);
          console.error(error);

          if (error.error.errors['AbsencePeriod'][0]) {
            this.toastService.showDanger('absencePeriodError', '', {
              shouldTranslate: true
            });
          } else {
            this.toastService.showRequestError();
          }
        }
      );
    } else {
      this.onSubmitSuccess();
    }
  }

  onSubmitSuccess(): void {
    this.resetFormState();
    this.toggleSectionLoading(false);
    this.toastService.showSuccess('editUserSuccess', '', {
      shouldTranslate: true
    });
  }

  cancelChanges(): void {
    this.absences = this.mapAbsences(this.originalAbsences);
    this.resetFormState();
  }

  private resetFormState(): void {
    this.sortAbsences();
    this.fillForm();
    this.form.markAsPristine();
    this.submitAttempted = false;
    this.deleteAbsenceIds = [];
    this.isOpenConfirmBack = false;
  }

  private mapAbsences(data: UserAbsenceListItem[]): AbsenceFormItem[] {
    return data.map((absence: UserAbsenceListItem) => ({
      id: absence.id,
      applicantUserId: absence.applicantUser?.id,
      substituteUserId: absence.substituteUser?.id ?? '',
      createdByUserId: absence.createdByUser?.id,
      periodStart: absence.periodStart || '',
      periodEnd: absence.periodEnd || null,
      isHistoric: isDateBeforeToday(absence.periodEnd || '')
    }));
  }

  submitAbsences(): Observable<any>[] {
    if (!checkClaims(this.POLICY_TYPE_ABSENCE, CLAIM_TYPES['create'])) {
      return [];
    }

    const absences = (this.absenceEntries.value as AbsenceFormItem[]).filter(
      (absence: AbsenceFormItem) =>
        (!isDateBeforeToday(String(absence.periodEnd)) || this.isUnmodifiedActive(absence)) && this.hasAbsenceChanged(absence)
    );

    return absences.map((absence: AbsenceFormItem) => {
      if (!absence.id) {
        if (this.applicantUserId && this.userId) {
          const args: CreateUserAbsenceRequest = {
            applicantUserId: this.userId,
            periodStart: absence.periodStart,
            periodEnd: absence.periodEnd,
            substituteUserId: absence.substituteUserId
          };

          return this.apiService.createUserAbsence(args);
        }

        return new Observable();
      } else {
        const args: UpdateUserAbsenceRequest = {
          id: absence.id,
          periodStart: absence.periodStart,
          periodEnd: absence.periodEnd,
          substituteUserId: absence.substituteUserId
        };

        return this.apiService.updateUserAbsence(args);
      }
    });
  }

  submitAbsenceDeletion(): Observable<any>[] {
    return this.deleteAbsenceIds.map((id: string) => {
      return this.apiService.deleteUserAbsence(id);
    });
  }

  submitUser(): Observable<any> {
    const submitData: UpdateUserRequest = {
      id: this.user?.id ?? '',
      memberships: this.getMemberships(),
      roles: this.getRoles(),
      email: this.user?.email || '',
      activationStatus: this.form.get('activationStatus')?.value
    };

    return this.apiService.updateUser(submitData);
  }

  back(confirmed: boolean = false): void {
    if (!this.form.dirty || confirmed) {
      this.location.back();
    } else {
      this.isOpenConfirmBack = true;
    }
  }

  addAbsence(): void {
    this.absenceEntries.push(
      this.formBuilder.group({
        periodStart: ['', Validators.required],
        periodEnd: [null],
        createdByUserId: [this.authService.getUserId() || ''],
        substituteUserId: [null, Validators.required]
      })
    );

    this.absences.push({
      periodStart: '',
      periodEnd: null,
      substituteUserId: '',
      applicantUserId: this.userId || ''
    });
    this.form.markAsDirty();
  }

  addRole(): void {
    const newRoleGroup = this.createRoleFormGroup({
      roleId: '',
      structuralUnitIds: []
    });

    this.roleEntries.push(newRoleGroup);
    const newIndex = this.roleEntries.length - 1;
    this.isStructuralUnitsCollapsed[newIndex] = true;

    // attach change handler for this specific role index
    this.attachRoleChangeHandler(newIndex);

    this.form.markAsDirty();
  }

  addStructuralRole(index: number): void {
    const entry = this.roleEntries.at(index) as FormGroup;
    const currentStructuralUnitIds = [...(entry.get('structuralUnitIds')?.value || [])];
    currentStructuralUnitIds.push('');
    this.isStructuralUnitsCollapsed[index] = true;
    entry.get('structuralUnitIds')?.setValue([...currentStructuralUnitIds]);
    this.form.markAsDirty();
  }

  getAbsenceStatus(index: number): string {
    const absence = this.absenceEntries.at(index)?.value;

    if (isDateBeforeToday(absence.periodEnd)) {
      return 'historic';
    } else if (absence.periodStart === '') {
      return 'empty';
    } else if (!isDateAfterToday(absence.periodStart) && (isDateTodayOrAfter(absence.periodEnd) || absence.periodEnd === null)) {
      return 'active';
    } else if (isDateAfterToday(absence.periodStart)) {
      return 'planned';
    }

    return 'active';
  }

  toggleStructuralUnitsCollapse(index: number): void {
    this.isStructuralUnitsCollapsed[index] = !this.isStructuralUnitsCollapsed[index];
  }

  absenceRangeChanged(range: { from: string | null; to: string | null }, index: number) {
    const absenceEntry = this.absenceEntries.at(index) as FormGroup;

    if (range.from) {
      absenceEntry.get('periodStart')?.setValue(range.from);

      this.absences[index].periodStart = range.from;
      this.absences[index].periodEnd = null;
    }

    if (range.from && range.to) {
      absenceEntry.get('periodEnd')?.setValue(range.to);

      this.absences[index].periodStart = range.from;
      this.absences[index].periodEnd = range.to;
    } else {
      absenceEntry.get('periodEnd')?.setValue(null);
    }

    this.form.markAsDirty();
  }

  deleteAbsence(index: number): void {
    const absenceEntry = this.absenceEntries.at(index) as FormGroup;

    if (absenceEntry.get('id')?.value) {
      this.deleteAbsenceIds.push(absenceEntry.get('id')?.value);
    }

    this.absenceEntries.removeAt(index);
    this.absences.splice(index, 1);
    this.form.markAsDirty();
  }

  deleteStructuralUnit(roleIndex: number, unitIndex: number): void {
    const entry = this.roleEntries.at(roleIndex) as FormGroup;
    const structuralUnitIds = entry.get('structuralUnitIds')?.value as number[];
    structuralUnitIds.splice(unitIndex, 1);
    entry.get('structuralUnitIds')?.setValue(structuralUnitIds);

    this.form.markAsDirty();
  }

  handleStructuralUnitChange(event: Event, roleIndex: number, structuralIndex: number): void {
    const id = (event.target as HTMLSelectElement).value;
    const entry = this.roleEntries.at(roleIndex) as FormGroup;
    const structuralUnitIds = entry.get('structuralUnitIds')?.value as string[];
    structuralUnitIds[structuralIndex] = id;
    entry.get('structuralUnitIds')?.setValue(structuralUnitIds);

    this.form.markAsDirty();
  }

  isSystemRole(id: string): boolean {
    return this.systemRoles.some((role: RoleListItem) => role.id === id);
  }

  getFormattedStructuralRoles(): any[] {
    const roleIdMap: number[] = [];

    this.user?.memberships?.forEach((membership: any) => {
      if (!roleIdMap.includes(membership.role.id)) {
        roleIdMap.push(membership.role.id);
      }
    });

    const formattedRoles: any[] = [];

    roleIdMap.forEach((id: number) => {
      const entries = this.user?.memberships?.filter((membership: any) => membership.role.id === id);

      const role = {
        roleId: id,
        structuralUnitIds: entries?.map((entry: any) => entry.structuralUnit.id)
      };

      formattedRoles.push(role);
    });

    return formattedRoles;
  }

  getMemberships(): any[] {
    const roles = this.roleEntries.value as any[];
    const memberships: UpdateUserMembershipItem[] = [];

    roles.forEach((role: any) => {
      if (!this.isSystemRole(role.roleId)) {
        role.structuralUnitIds.forEach((structUnitId: any) => {
          memberships.push({
            structuralUnitId: structUnitId,
            roleId: role.roleId
          });
        });
      }
    });

    return memberships;
  }

  getRoles() {
    const roles = this.roleEntries.value as any[];
    const roleIds: any = [];

    roles.forEach((role: any) => {
      if (this.isSystemRole(role.roleId)) {
        roleIds.push(role);
      }
    });

    return roleIds;
  }

  canEditRole(roleId: string): boolean {
    const role = this.systemRoles.find((role: RoleListItem) => role.id === roleId);

    return role?.name !== SYSTEM_USER_ROLE_NAME;
  }

  isUnitOptionDisabled(parentIndex: number, unitId: string): boolean {
    const entry = this.roleEntries.at(parentIndex) as FormGroup;
    const structuralUnitIds = entry.get('structuralUnitIds')?.value as string[];

    return structuralUnitIds.includes(unitId);
  }

  isRoleOptionDisabled(roleId: string): boolean {
    const roles = this.roleEntries.value as any[];

    return roles.some((role: any) => role.roleId === roleId);
  }

  shouldRenderError(key: string, index: number): boolean {
    if (key.startsWith('roles.')) {
      const groupKey = key.split('.')[1];
      const roleEntry = this.roleEntries.at(index) as FormGroup;
      const field = roleEntry.get(groupKey);

      return !!field?.invalid && (!!field?.touched || this.submitAttempted) && !!field?.errors;
    }

    if (key.startsWith('absences.')) {
      const groupKey = key.split('.')[1];
      const absenceEntry = this.absenceEntries.at(index) as FormGroup;
      const field = absenceEntry.get(groupKey);

      return !!field?.invalid && (!!field?.touched || this.submitAttempted) && !!field?.errors;
    }

    return this.submitAttempted;
  }

  getFormErrors(form: FormGroup | FormArray): any {
    const errors: any = {};
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      if (control instanceof FormGroup || control instanceof FormArray) {
        errors[key] = this.getFormErrors(control);
      } else if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }

  getStructuralUnitIdAtIndex(parentIndex: number, index: number): string {
    const entry = this.roleEntries.at(parentIndex) as FormGroup;
    const structuralUnitIds = entry.get('structuralUnitIds')?.value as string[];

    return structuralUnitIds[index];
  }

  getUserFullName(userId: string): string {
    const user = this.allUsers?.find((user: UserListItem) => user.id === userId);
    return user?.fullName || '-';
  }

  deleteRole(index: number) {
    if (this.roleIdSubs[index]) {
      this.roleIdSubs[index].unsubscribe();
      this.roleIdSubs.splice(index, 1);
    }

    // 2) remove the role form group (this also removes its structuralUnitIds)
    this.roleEntries.removeAt(index);

    // 3) reindex collapse map and subscriptions alignment
    this.rebuildCollapseMap();

    // 4) Ensure remaining subs align to indices (re-attach to be safe)
    this.roleIdSubs.forEach((s) => s?.unsubscribe());
    this.roleIdSubs = [];
    this.roleEntries.controls.forEach((_, i) => this.attachRoleChangeHandler(i));

    this.form.updateValueAndValidity();
    this.form.markAsDirty();
  }

  getDisabledDates(index: number): NgbDate[] {
    const absenceAtIndex = this.absenceEntries.at(index)?.value;
    const absenceDateRange: NgbDate[] = [];
    const disabledDates: NgbDate[] = [];

    this.absences.forEach((absence: AbsenceFormItem) => {
      if (absence.periodEnd && !this.deleteAbsenceIds.includes(absence.id!)) {
        const startDate = new Date(absence.periodStart);
        const endDate = new Date(absence.periodEnd);

        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          disabledDates.push(
            new NgbDate(
              currentDate.getFullYear(),
              currentDate.getMonth() + 1, // Months are 0-indexed in JavaScript
              currentDate.getDate()
            )
          );

          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    });

    if (absenceAtIndex.periodEnd) {
      const startDate = new Date(absenceAtIndex.periodStart);
      const endDate = new Date(absenceAtIndex.periodEnd);

      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        absenceDateRange.push(
          new NgbDate(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1, // Months are 0-indexed in JavaScript
            currentDate.getDate()
          )
        );

        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    return disabledDates.filter((date: NgbDate) => !absenceDateRange.some((absenceDate: NgbDate) => date.equals(absenceDate)));
  }

  getMaxDateAtIndex(index: number): NgbDateStruct {
    const absenceAtIndex = this.absenceEntries.at(index)?.value;
    if (!absenceAtIndex.periodEnd) {
      return { year: 2099, month: 12, day: 31 };
    }

    // Find the earliest start date in `absences` and subtract one day
    const absenceWithStart = this.absences.find((absence: AbsenceFormItem) => {
      return !absence.periodEnd && absence.periodStart && absence.periodStart !== '';
    });

    if (absenceWithStart?.periodStart) {
      const date = new Date(absenceWithStart.periodStart);
      date.setDate(date.getDate() - 1);

      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      };
    }

    return { year: 2099, month: 12, day: 31 };
  }

  onModalClose(): void {
    this.isOpenConfirmBack = false;
  }

  getSystemUserId(): string {
    return this.systemRoles.find((role: RoleListItem | any) => role.name === 'Sistēmas lietotājs')?.id || '';
  }

  sortAbsences(): void {
    this.absences.sort((a: AbsenceFormItem, b: AbsenceFormItem) => {
      return new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime();
    });
  }

  hasHistoricalAbsences(): boolean {
    return this.absenceEntries.controls.filter((absence) => isDateBeforeToday(absence.get('periodEnd')?.value)).length > 0;
  }

  hasEditRoleClaim(): boolean {
    return checkClaims(this.POLICY_TYPE_ROLE, CLAIM_TYPES['update']);
  }

  hasEditUserClaim(): boolean {
    return checkClaims(this.POLICY_TYPE_USER, CLAIM_TYPES['update']);
  }

  hasReadUserClaim(): boolean {
    return checkClaims(this.POLICY_TYPE_USER, CLAIM_TYPES['read']);
  }

  get isMyProfileOnly(): boolean {
    return !this.hasReadUserClaim() && this.userId === this.authService.getUserId();
  }

  getRoleName(roleId: string): string {
    const role = this.allRoles.find((role: RoleListItem | StructuralUnitRoleListItem) => role.id === roleId);
    return role?.name || '';
  }

  getStructuralUnitName(unitId: string): string {
    const unit = this.structuralUnitOptions.find((unit: StructuralUnitMinimalListItem) => unit.id === unitId);
    return unit?.name || '';
  }

  hasAbsenceChanged(updatedAbsence: AbsenceFormItem): boolean {
    const currentAbsence = this.originalAbsences.find((a: UserAbsenceListItem) => a.id === updatedAbsence.id);

    return (
      currentAbsence?.periodStart !== updatedAbsence.periodStart ||
      currentAbsence?.periodEnd !== updatedAbsence.periodEnd ||
      currentAbsence?.substituteUser?.id !== updatedAbsence.substituteUserId
    );
  }

  isUnmodifiedActive(formAbsence: AbsenceFormItem): boolean {
    const originalAbsence = this.originalAbsences.find((absence: UserAbsenceListItem) => absence.id === formAbsence.id);

    if (!originalAbsence) {
      return false;
    }

    return !isDateBeforeToday(originalAbsence.periodEnd || '');
  }

  private attachRoleChangeHandler(roleIndex: number): void {
    const group = this.roleEntries.at(roleIndex) as FormGroup;
    const roleIdCtrl = group.get('roleId');

    if (this.roleIdSubs[roleIndex]) {
      this.roleIdSubs[roleIndex].unsubscribe();
    }

    this.roleIdSubs[roleIndex] = roleIdCtrl!.valueChanges.subscribe((newRoleId: string) => {
      group.get('structuralUnitIds')?.setValue([]);

      if (!this.isSystemRole(newRoleId)) {
        this.addStructuralRole(roleIndex);
      }

      // Keep UI state consistent
      this.isStructuralUnitsCollapsed[roleIndex] = true;
      this.form.markAsDirty();
    });
  }

  private rebuildCollapseMap(): void {
    const newMap: Record<number, boolean> = {};
    this.roleEntries.controls.forEach((_, i) => {
      newMap[i] = this.isStructuralUnitsCollapsed[i] ?? true;
    });
    this.isStructuralUnitsCollapsed = newMap;
  }
}
